import type { SentSms, SentSmsPage, SmsSendView, SmsSendViewItem } from "@/types/SentSms";
import { apiGet } from "../_apiShim";

const BASE_PATH = "/api/v1/sms-sent";

/**
 * The SMS rows of one guest, newest first as the backend orders them
 * (WORKFLOWS_READ). Throws the backend's code on `.message` (rethrowApi) —
 * a failed read is NOT an empty history, the caller says so.
 */
export async function getSentSmsForGuest(
  eventId: string,
  guestId: string,
): Promise<SentSms[]> {
  const rows = await apiGet<SentSms[]>(
    `${BASE_PATH}/${encodeURIComponent(eventId)}/guest/${encodeURIComponent(guestId)}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  return Array.isArray(rows) ? rows : [];
}

/** The page size the SMS page asks for (the backend caps `limit` at 100). */
export const EVENT_SENT_SMS_PAGE_SIZE = 50;

/**
 * One page of the event's SMS log (SMS plugin wave, BE-5) — every guest-mode
 * and team-number row of the event the viewer may see, newest first
 * (WORKFLOWS_READ, row scope applied server-side). `cursor` = the previous
 * page's `nextCursor`, absent for the first page. Throws the backend's code
 * on `.message` (rethrowApi): a failed read is not an empty log.
 *
 * Read defensively: a body without `items` is an empty page, and anything but
 * a non-empty string cursor is the end.
 */
export async function getEventSentSms(
  eventId: string,
  cursor: string | null,
  limit: number = EVENT_SENT_SMS_PAGE_SIZE,
): Promise<SentSmsPage> {
  const params = new URLSearchParams({ limit: String(Math.min(100, Math.max(1, limit))) });
  if (cursor) params.set("cursor", cursor);
  const body = await apiGet<Partial<SentSmsPage> | null>(
    `${BASE_PATH}/${encodeURIComponent(eventId)}?${params.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  const items = Array.isArray(body?.items) ? body.items : [];
  const nextCursor = typeof body?.nextCursor === "string" && body.nextCursor.length > 0 ? body.nextCursor : null;
  return { items, nextCursor };
}

const OBJECT_ID = /^[0-9a-f]{24}$/;

/** Is this a job id the per-send view can be opened with (lowercase 24-hex)? */
export function isSmsSendId(value: unknown): value is string {
  return typeof value === "string" && OBJECT_ID.test(value);
}

function countsOf(raw: unknown): Record<string, number> | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out: Record<string, number> = {};
  for (const [code, n] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof n === "number" && Number.isFinite(n) && n > 0) out[code] = Math.floor(n);
  }
  return out;
}

const strOf = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const numOf = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/**
 * The per-send view of one manual send (SMS manual send, B-3 / F-4) — one
 * page, the send's rows + skips and the job's counters. Throws the backend's
 * code on `.message` (rethrowApi): a failed read is not an empty send.
 *
 * Read defensively: anything but an object for the counts is "not available",
 * a missing `jobAvailable` / `sendAvailable` is "not there", a missing counter
 * is `null` (a row-restricted reader gets none of the job's counters — the
 * page counts its rows instead, audit r1 FD-1).
 *
 * Only an answer about THIS send is one (audit r1 C2): a backend without the
 * `?send=` read ignores the parameter and answers the event's whole log page
 * `{items, nextCursor}` — its rows, from every automation and send, would read
 * as this send's guests. A body that names another job, or none, is "nothing
 * there" (the page says "not found").
 */
export async function getSmsSendView(eventId: string, jobId: string): Promise<SmsSendView> {
  const params = new URLSearchParams({ send: jobId });
  const body = await apiGet<Record<string, unknown> | null>(
    `${BASE_PATH}/${encodeURIComponent(eventId)}?${params.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  const raw = body && typeof body === "object" ? body : {};
  const aboutThisSend = typeof raw.jobId === "string" && raw.jobId.toLowerCase() === jobId.toLowerCase();
  const b: Record<string, unknown> = aboutThisSend ? raw : {};
  const items = Array.isArray(b.items)
    ? (b.items as unknown[]).filter((i): i is SmsSendViewItem => !!i && typeof i === "object")
    : [];
  return {
    jobId: strOf(b.jobId),
    sendAvailable: b.sendAvailable === true,
    sendName: strOf(b.sendName),
    recipients: numOf(b.recipients),
    createdAt: strOf(b.createdAt),
    jobAvailable: b.jobAvailable === true,
    status: strOf(b.status),
    total: numOf(b.total),
    processedCount: numOf(b.processedCount),
    failedCount: numOf(b.failedCount),
    cancelledCount: numOf(b.cancelledCount),
    skippedCount: numOf(b.skippedCount),
    dryrunCount: numOf(b.dryrunCount),
    smsSkipCounts: countsOf(b.smsSkipCounts),
    smsHaltReason: strOf(b.smsHaltReason),
    smsHaltAt: strOf(b.smsHaltAt),
    endReason: strOf(b.endReason),
    items,
  };
}
